import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import { api } from '../APIs/api';
import { API_URL, BASE_URL } from '../APIs/endpoint';
import DynamicBanner from '../components/DynamicDestination/DynamicBanner';
import DynamicCustomizedHolidays from '../components/DynamicDestination/DynamicCustomizedHolidays';
import DynamicPageDescription from '../components/DynamicDestination/DynamicPageDescription';
import DynamicPhotoGallery from '../components/DynamicDestination/DynamicPhotoGallery';
import BusinessCount from '../components/Home/BusinessCount/BusinessCount';

const DynamicDestinationPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessCount, setBusinessCount] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/cms-page/${slug}`;
        const response = await axios.get(url);
        setPageData(response.data.data);
      } catch (error) {
        console.error('API Error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPageData();
  }, [slug]);

  useEffect(() => {
    const fetchBusinessCount = async () => {
      try {
        const businessCountRes = await api.get(`${BASE_URL}${API_URL.BUSINESS_COUNT}`);
        const { data, status } = businessCountRes;
        if (status) {
          setBusinessCount(data);
        }
      } catch (error) {
        console.error('Business count API Error:', error);
      }
    };
    fetchBusinessCount();
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const isReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';

    if (isReload) {
      sessionStorage.removeItem('dynamicDestinationMenu');
    }

    const forceTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Only auto-scroll to a section for in-app menu navigation, never on reload
    const target = !isReload ? sessionStorage.getItem('dynamicDestinationMenu') : null;

    if (!target) {
      forceTop();
      const timers = [0, 50, 150, 400, 800, 1500].map((delay) => setTimeout(forceTop, delay));
      return () => timers.forEach(clearTimeout);
    }

    return undefined;
  }, [slug]);

  useEffect(() => {
    if (!pageData) return;

    const isReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';

    if (isReload) {
      sessionStorage.removeItem('dynamicDestinationMenu');
      window.scrollTo(0, 0);
      return;
    }

    const target = sessionStorage.getItem('dynamicDestinationMenu');
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }

    const SCROLL_OFFSET = 100;

    const scrollToSection = (sectionId) => {
      const element = document.getElementById(sectionId);
      if (!element) return;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - SCROLL_OFFSET,
        behavior: 'smooth'
      });
    };

    const timer = setTimeout(() => {
      if (target === 'photo-gallery') {
        scrollToSection('photoGallerySection');
      } else if (target === 'customized-holidays') {
        scrollToSection('packagesSection');
      } else if (target === 'reviews') {
        scrollToSection('reviewCard');
      }
      sessionStorage.removeItem('dynamicDestinationMenu');
    }, 300);

    return () => clearTimeout(timer);
  }, [pageData]);

  if (isLoading) return null;
  if (!pageData) return null;

  return (
    <div style={{ backgroundColor: '#f4f4f7' }}>
      <DynamicBanner images={pageData.images || []} mobileBanner={pageData.mobile_banner || []} />

      <Box id="whyChooseUsSection" sx={{ py: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            px: { xs: 2, sm: 3, md: 4 }
          }}>
          <Box sx={{ flex: 1, height: '2px', backgroundColor: '#000' }} />
          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '16px', md: '18px' },
              fontFamily: 'gothamBold',
              textAlign: 'center',
              color: '#27306B',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              px: 3
            }}>
            Why Choose Us?
          </Typography>
          <Box sx={{ flex: 1, height: '2px', backgroundColor: '#000' }} />
        </Box>
        <BusinessCount businessCount={businessCount} />
      </Box>

      <DynamicPageDescription description={pageData.page_description || ''} />

      <DynamicCustomizedHolidays mainContent={pageData.main_content || []} />

      <DynamicPhotoGallery gallery={pageData.gallery || []} />
    </div>
  );
};

export default DynamicDestinationPage;
